import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackWhatsAppClick } from '../utils/analytics';

interface WhatsAppFloatProps {
  phoneNumber: string;
  message?: string;
  showAfterSeconds?: number;
}

const WhatsAppFloat: React.FC<WhatsAppFloatProps> = ({
  phoneNumber,
  message = "Hallo! Ich interessiere mich für Ihre Angebote.",
  showAfterSeconds = 3
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), showAfterSeconds * 1000);
    return () => clearTimeout(timer);
  }, [showAfterSeconds]);

  const formatPhoneNumber = (phone: string) =>
    phone.replace(/\D/g, '').replace(/^0/, '49');

  const formattedPhone = formatPhoneNumber(phoneNumber);
  const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence mode="wait">
        {isVisible && isMinimized && (
          <motion.button
            key="minimized"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsMinimized(false)}
            className="bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg"
            title="WhatsApp Chat öffnen"
          >
            <motion.span
              animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 4 }}
              className="block"
            >
              <MessageCircle className="w-6 h-6" />
            </motion.span>
          </motion.button>
        )}

        {isVisible && !isMinimized && (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="bg-white rounded-xl shadow-2xl border border-gray-100 p-4 max-w-sm w-72"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <motion.div
                  className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center"
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <MessageCircle className="w-5 h-5 text-white" />
                </motion.div>
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">WhatsApp Support</h4>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <p className="text-xs text-green-600">Online</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsMinimized(true)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Haben Sie Fragen? Schreiben Sie uns direkt über WhatsApp!
            </p>

            <motion.a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => trackWhatsAppClick('whatsapp_float')}
              className="block w-full bg-green-500 hover:bg-green-600 text-white text-center py-2.5 px-4 rounded-lg font-medium text-sm transition-colors duration-300"
            >
              Chat starten
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WhatsAppFloat;
